import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, Rows, List, Grid, LayoutGrid, User, Calendar, CheckCircle, Bell, Eye, ClipboardList, Copy, ExternalLink, CalendarPlus, Download, ListTodo, MoreHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { mockEvents } from './mockEvents';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

// Firebase imports
import { auth } from '../../../js/external-js/config/main_config.js';

// Service imports
import { getComplianceData, getUserComplianceEvents } from '../../../services/complianceService.js';

// Tenancy context import
import { useTenancy } from '../../../contexts/TenancyContext.js';

import PerformTaskDialog from './PerformTaskDialog';
import ReviewAssetDialog from './ReviewAssetDialog';
import ReviewRiskDialog from './ReviewRiskDialog';
import ReviewDocDialog from './ReviewDocDialog';
//import PolicyDialog from './PolicyDialog';
import PolicyReviewDialog from './PolicyReviewDialog';
import ChecklistCompleteDialog from '@/app/pages/checklists/components/checklist-complete-dialog';
import CorrectiveActionDialog from './CorrectiveActionDialog';
import { ReviewProcessDialog } from './ReviewProcessDialog';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import AddAssuranceTaskModal from '@/app/pages/assurancelibrary/AddAssuranceTaskModal';
import { deiterateAPI } from '../../../js/external-js/variableModules/utils/deiterateAPI.util.js';
import { toast } from 'sonner';
import { checklistService } from '@/app/pages/checklists/services/checklist-service';
import type { Checklist } from '@/app/pages/checklists/types/checklist.types';
import type { Finding } from '@/app/pages/auditcentre';
import type { Assignee, ComplianceEvent } from '@/types/compliance';
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

// Calendar Views
const VIEWS = ['Day', 'Week', 'Month', 'Monthly List', 'Year'] as const;
type CalendarView = typeof VIEWS[number];

const today = new Date();
const userLocale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-AU'; // Use browser's locale

function getTitle(view: CalendarView, date: Date) {
  switch (view) {
    case 'Day':
      return date.toLocaleDateString(userLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    case 'Week': {
      const start = new Date(date);
      start.setDate(date.getDate() - date.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString(userLocale, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(userLocale, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    case 'Month':
      return date.toLocaleDateString(userLocale, { month: 'long', year: 'numeric' });
    case 'Year':
      return date.getFullYear();
    case 'Monthly List':
      return date.toLocaleDateString(userLocale, { month: 'long', year: 'numeric' });
  }
}

// --- Event type color, label, icon maps ---
const eventTypeStyles = {
  'assurance-task': 'bg-orange-100 text-orange-800',
  'asset-review': 'bg-blue-100 text-blue-800',
  'risk-review': 'bg-yellow-100 text-yellow-800',
  'document-review': 'bg-purple-100 text-purple-800',
  'policy-review': 'bg-green-100 text-green-800',
  'checklist': 'bg-green-100 text-green-800',
  'corrective-action': 'bg-red-100 text-red-800',
  'process': 'bg-emerald-100 text-emerald-800',
};

const eventTypeLabels = {
  'assurance-task': 'Assurance Task',
  'asset-review': 'Asset Review',
  'risk-review': 'Risk Review',
  'document-review': 'Document Review',
  'policy-review': 'Policy Review',
  'checklist': 'Checklist',
  'corrective-action': 'Corrective Action',
  'process': 'Process',
};

const eventTypeIcons = {
  'assurance-task': <CheckCircle className="w-5 h-5 text-orange-400" />, // orange check
  'asset-review': <User className="w-5 h-5 text-blue-400" />, // blue user
  'risk-review': <Bell className="w-5 h-5 text-yellow-400" />, // yellow bell
  'document-review': <Calendar className="w-5 h-5 text-purple-400" />, // purple calendar
  'policy-review': <Calendar className="w-5 h-5 text-green-400" />, // green calendar
  'checklist': <Calendar className="w-5 h-5 text-green-400" />, // green calendar
  'corrective-action': <ClipboardList className="w-5 h-5 text-red-400" />, // red clipboard
  'process': <ClipboardList className="w-5 h-5 text-emerald-400" />, // emerald clipboard for processes
};

const USER_FILTERS = [
  { label: 'All Events', value: 'all' },
  { label: 'My Events', value: 'mine' }
];

let tenancyCurrentUser: Assignee | null = null;
let userIdToNameMap: Record<string, string> = {};

// Helper to check if a string looks like a user ID (not a display name)
// User IDs typically don't contain spaces and are alphanumeric
const looksLikeUserId = (str: string): boolean => {
  if (!str) return false;
  // If it contains a space, it's likely a display name
  if (str.includes(' ')) return false;
  // If it's a common placeholder, treat as display name
  if (['Unassigned', 'Unknown', 'N/A', 'None'].includes(str)) return false;
  return true;
};

// Helper to get display name from assignee (which might be UID or already a name)
const getAssigneeName = (assignee: string): string => {
  if (!assignee) return '';
  // Only look up in userIdToNameMap if it looks like a UID
  if (looksLikeUserId(assignee)) {
    return userIdToNameMap[assignee] || assignee;
  }
  // Already a display name, return as-is
  return assignee;
};

const ComplianceCalendar: React.FC = () => {
  const navigate = useNavigate();
  const fetchIdRef = React.useRef(0);
  const [view, setView] = useState<CalendarView>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('complianceCalendarView');
      if (stored && VIEWS.includes(stored as CalendarView)) {
        return stored as CalendarView;
      }
    }
    return 'Month';
  });
  // State for Add Assurance Task Modal
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date>(today);
  const [performOpen, setPerformOpen] = useState(false);
  const [reviewAssetOpen, setReviewAssetOpen] = useState(false);
  const [reviewRiskOpen, setReviewRiskOpen] = useState(false);
  const [reviewDocOpen, setReviewDocOpen] = useState(false);
  const [outlookHelpOpen, setOutlookHelpOpen] = useState(false);
  const [googleHelpOpen, setGoogleHelpOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyReviewOpen, setPolicyReviewOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [correctiveActionOpen, setCorrectiveActionOpen] = useState(false);
  const [processOpen, setProcessOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ComplianceEvent | null>(null);
  const [userFilter, setUserFilter] = useState<'all' | 'mine'>('all');

  // New state variables for data handling
  const [events, setEvents] = useState<ComplianceEvent[]>([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);
  // Mock data toggle is disabled - keeping for reference only
  // const [useMockData, setUseMockData] = useState(false);
  const [icsUrl, setIcsUrl] = useState<string | null>(null);
  const [signingIcs, setSigningIcs] = useState(false);
  const [exportingProgress, setExportingProgress] = useState(false);

  // Get current user ID from Firebase auth
  const currentUserId = auth.currentUser?.uid || null;

  // For filtering purposes - get a reasonable user name
  const currentUser = auth.currentUser?.displayName || 'John S.';

  // Get active tenancy from context
  const { activeTenancy, currentUser: tenancyUser, userIdToName } = useTenancy();
  // Update module-level map for use in DayView/MonthlyListView
  userIdToNameMap = userIdToName || {};
  const derivedTenancyUser = React.useMemo<Assignee | null>(() => {
    if (!tenancyUser) return null;
    const source = tenancyUser as Record<string, any>;
    const userID: string = source.userID ?? source.uid ?? source.id ?? '';
    const firstName: string = source.firstName ?? '';
    const lastName: string = source.lastName ?? '';
    if (!userID && !firstName && !lastName) {
      return null;
    }
    return {
      userID,
      firstName,
      lastName,
      companyName: source.companyName ?? undefined,
      mspUser: source.mspUser ?? undefined,
      mspUsers: source.mspUsers ?? undefined,
    } satisfies Assignee;
  }, [tenancyUser]);
  tenancyCurrentUser = derivedTenancyUser;

  // Helper to check if the current tenancy is a starter plan
  const isStarterPlan = React.useMemo(() => {
    if (!activeTenancy?.plan) return false;
    // Starter plan is anything that is NOT Business Plan or Enterprise Plan
    const isIsoPlan = activeTenancy.plan === 'Business Plan' || activeTenancy.plan === 'Enterprise Plan';
    return !isIsoPlan;
  }, [activeTenancy?.plan]);

  // --- Refresh events function for dialogs to trigger reload ---
  const refreshEvents = React.useCallback(async () => {
    if (!activeTenancy || !activeTenancy.id) {
      setError("No company ID found. Please select a tenancy.");
      setLoading(false);
      return [];
    }
    setLoading(true);
    setError(null);
    const companyId = activeTenancy.id;
    const dateFilter = getDateRangeForView(view, currentDate);
    const filter = {
      startDate: dateFilter.startDate,
      endDate: dateFilter.endDate,
      view: view.toLowerCase() as 'day' | 'week' | 'month' | 'year',
    };
    try {
      const data = await getComplianceData(companyId, filter);
      setEvents(data.allEvents);
      setLoading(false);
      return data.allEvents;
    } catch (err) {
      setError("Failed to load compliance data. Please try again later.");
      setEvents([]);
      setLoading(false);
      return [];
    }
  }, [activeTenancy, view, currentDate]);

  // Cleaned: Only refresh events, do not update selectedEvent live (this used to do alot more. might be able to be removed)
  const handleDataChanged = React.useCallback(async () => {
    const allEvents = await refreshEvents();
    // Always update selectedEvent from the freshest data after event list refresh
    if (selectedEvent && allEvents && allEvents.length > 0) {
      const updated = allEvents.find(ev => ev.id === selectedEvent.id);
      setSelectedEvent(updated || null);
    }
  }, [refreshEvents, selectedEvent]);

  // New helper function to wait for window property to be available
  const waitForWindowProperty = (propertyName: string, maxWaitTime = 10000, checkInterval = 200): Promise<any> => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkProperty = () => {
        // Check if property exists on window
        if ((window as any)[propertyName] !== undefined) {
          resolve((window as any)[propertyName]);
          return;
        }

        // Check if we've exceeded max wait time
        if (Date.now() - startTime > maxWaitTime) {
          reject(new Error(`Timed out waiting for window.${propertyName}`));
          return;
        }

        // Try again after interval
        setTimeout(checkProperty, checkInterval);
      };

      // Start checking
      checkProperty();
    });
  };

  // Helper function to get date range for current view
  const getDateRangeForView = (view: CalendarView, date: Date): { startDate: Date, endDate: Date } => {
    const startDate = new Date(date);
    const endDate = new Date(date);

    switch (view) {
      case 'Day':
        // Just the single day
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'Week':
        // Start of week (Sunday) to end of week (Saturday)
        startDate.setDate(date.getDate() - date.getDay()); // Go back to Sunday
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(startDate.getDate() + 6); // Saturday
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'Month':
        // Start of month to end of month
        startDate.setDate(1); // First day of month
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(date.getMonth() + 1, 0); // Last day of month
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'Year':
        // Full year
        startDate.setMonth(0, 1); // January 1
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(11, 31); // December 31
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'Monthly List':
        // Same as Month
        startDate.setDate(1); // First day of month
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(date.getMonth() + 1, 0); // Last day of month
        endDate.setHours(23, 59, 59, 999);
        break;
    }

    return { startDate, endDate };
  };

  // --- ICS Subscribe helpers ---
  const getApiBaseUrl = (): string => {
    try {
      const w = (window as any);
      if (w && w.apiURL) return w.apiURL as string;
    } catch {}
    // Fallback to env if window.apiURL is missing
    return (import.meta as any).env.VITE_cloudFunctionURL + '/deiterateInternalAPI';
  };

  const buildFullIcsUrl = (path: string): string => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path; // already absolute
    const base = getApiBaseUrl().replace(/\/$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  };

  const signIcsLink = async (): Promise<string | null> => {
    if (!activeTenancy || !activeTenancy.id) {
      toast.error('No company selected. Please select a tenancy.');
      return null;
    }
    try {
      setSigningIcs(true);
      const body: any = {
        name: 'Compliance Calendar'
      };

      const res: any = await deiterateAPI.POST('/calendar/ics/compliance/token', {
        body: JSON.stringify(body),
        companyID: activeTenancy.id,
      });

      const fullUrl = buildFullIcsUrl(res?.path || '');
      if (!fullUrl) throw new Error('Empty path returned from signer');
      setIcsUrl(fullUrl);
      return fullUrl;
    } catch (err) {
      console.error('Failed to sign ICS URL:', err);
      toast.error('Failed to generate subscription link');
      return null;
    } finally {
      setSigningIcs(false);
    }
  };

  const ensureIcsUrl = async (): Promise<string | null> => {
    if (icsUrl) return icsUrl;
    return await signIcsLink();
  };

  const copySubscriptionLink = async () => {
    const url = await ensureIcsUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Subscription link copied to clipboard');
    } catch {
      // Fallback: show URL in toast if clipboard fails
      toast.success(`Subscription link ready: ${url}`);
    }
  };

  const openInGoogleCalendar = async () => {
    setGoogleHelpOpen(true);
  };

  const openInAppleCalendar = async () => {
    const url = await ensureIcsUrl();
    if (!url) return;
    const webcalUrl = url.replace(/^https?:\/\//i, 'webcal://');
    window.open(webcalUrl, '_blank');
  };

  const openInOutlook365 = async () => {
    setOutlookHelpOpen(true);
  };

  const confirmOutlook365 = async () => {
    const url = await ensureIcsUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Subscription URL copied to clipboard');
    } catch {
      toast.success(`Subscription URL: ${url}`);
    }
    try {
      // Fire-and-forget fetch to ensure lastUsedAt is updated immediately
      void fetch(url, { mode: 'no-cors' });
    } catch {}
    window.open('https://outlook.office.com/calendar/addcalendar', '_blank', 'noopener');
    setOutlookHelpOpen(false);
  };

  const confirmGoogleCalendar = async () => {
    const url = await ensureIcsUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Subscription URL copied to clipboard');
    } catch {
      toast.success(`Subscription URL: ${url}`);
    }
    try {
      // Fire-and-forget fetch to ensure lastUsedAt is updated immediately
      void fetch(url, { mode: 'no-cors' });
    } catch {}
    window.open('https://calendar.google.com/calendar/u/0/r/settings/addbyurl', '_blank', 'noopener');
    setGoogleHelpOpen(false);
  };

  // Export progress handler - exports assurance tasks to .docx
  const handleExportProgress = async () => {
    if (!activeTenancy?.id) {
      toast.error('No company selected. Please select a tenancy.');
      return;
    }
    
    try {
      setExportingProgress(true);
      
      // Import the generator dynamically to avoid loading it unnecessarily
      const { generateDocument } = await import('../../../js/external-js/variableModules/utils/assuranceTasksGenerator.util.js');
      
      // Fetch assurance tasks from Firestore
      const { db } = await import('../../../js/external-js/config/main_config.js');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const assuranceDocRef = doc(db, `customers/${activeTenancy.id}/compliance/assurance`);
      const assuranceSnapshot = await getDoc(assuranceDocRef);
      const assuranceData = assuranceSnapshot.data() || {};
      
      // Format assurance tasks for the document
      const formattedTasks = Object.entries(assuranceData)
        .filter(([_, task]: [string, any]) => task?.checkDetail?.active !== false)
        .map(([taskID, task]: [string, any]) => ({
          taskID,
          taskName: task?.checkDetail?.check || 'Unnamed Task',
          taskDescription: task?.checkDetail?.description || '',
          frequency: task?.checkDetail?.frequency || 'N/A',
          assignee: task?.checkDetail?.assignee || 'Unassigned',
          status: task?.checkFrequency?.lastCheck ? 'Complete' : 'Pending',
          SupportingDocs: task?.checkDetail?.supportingDocs || [],
          evidenceData: [],
        }))
        .sort((a, b) => {
          const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
          return collator.compare(a.taskID, b.taskID);
        });
      
      const templateData = {
        CompanyFriendlyName: activeTenancy.name || 'Company',
        CompanyLegalName: activeTenancy.legalName || activeTenancy.name || 'Company',
        standardsImplemented: [],
        controls: [],
        assuranceTasks: formattedTasks,
      };
      
      // Generate and save the document
      await generateDocument(templateData, true);
      toast.success('Progress exported successfully');
    } catch (error) {
      console.error('Error exporting progress:', error);
      toast.error('Failed to export progress. Please try again.');
    } finally {
      setExportingProgress(false);
    }
  };

  // Navigation handlers
  const goToPrev = () => {
    const d = new Date(currentDate);
    if (view === 'Day') d.setDate(d.getDate() - 1);
    else if (view === 'Week') d.setDate(d.getDate() - 7);
    else if (view === 'Month') d.setMonth(d.getMonth() - 1);
    else if (view === 'Year') d.setFullYear(d.getFullYear() - 1);
    else if (view === 'Monthly List') d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };
  const goToNext = () => {
    const d = new Date(currentDate);
    if (view === 'Day') d.setDate(d.getDate() + 1);
    else if (view === 'Week') d.setDate(d.getDate() + 7);
    else if (view === 'Month') d.setMonth(d.getMonth() + 1);
    else if (view === 'Year') d.setFullYear(d.getFullYear() + 1);
    else if (view === 'Monthly List') d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };
  const goToToday = () => setCurrentDate(today);

  // Handler to open perform dialog
  const loadChecklistForEvent = React.useCallback(async (event: ComplianceEvent) => {
    if (!activeTenancy?.id) {
      toast.error('Select a tenancy before opening checklists.');
      return;
    }
    const sourceId = event?.checklistId || event?.id;
    if (!sourceId) {
      toast.error('Checklist is missing an identifier.');
      setSelectedChecklist(null);
      return;
    }

    setLoadingChecklist(true);
    try {
      const checklist = await checklistService.getChecklist(sourceId, activeTenancy.id);
      if (!checklist) {
        toast.error('Checklist could not be loaded.');
        setSelectedChecklist(null);
        return;
      }
      setSelectedChecklist(checklist);
      setSelectedEvent(event);
    } catch (error) {
      console.error('[ComplianceCalendar] Failed to load checklist for event:', error);
      toast.error('Failed to load checklist. Please try again.');
      setSelectedChecklist(null);
    } finally {
      setLoadingChecklist(false);
    }
  }, [activeTenancy?.id]);

  const handleChecklistSubmit = React.useCallback(async (values: Record<string, any>, findings?: Finding[]) => {
    if (!activeTenancy?.id) {
      toast.error('Select a tenancy before submitting checklists.');
      return;
    }
    if (!selectedChecklist?.id) {
      toast.error('No checklist selected.');
      return;
    }
    const userId = auth.currentUser?.uid;
    if (!userId) {
      toast.error('You must be signed in to complete a checklist.');
      return;
    }

    const valuesWithTitles: Record<string, { title: string; value: any }> = {};
    selectedChecklist.items?.forEach(item => {
      if (Object.prototype.hasOwnProperty.call(values, item.id)) {
        valuesWithTitles[item.id] = { title: item.title, value: values[item.id] };
      }
      const evidenceKey = `evidence-${item.id}`;
      if (Object.prototype.hasOwnProperty.call(values, evidenceKey)) {
        valuesWithTitles[evidenceKey] = { title: `${item.title} evidence`, value: values[evidenceKey] };
      }
    });

    try {
      await checklistService.submitChecklist(
        selectedChecklist.id,
        userId,
        activeTenancy.id,
        undefined,
        valuesWithTitles,
        findings
      );
      toast.success('Checklist submitted.');
      setChecklistOpen(false);
      setSelectedChecklist(null);
      await handleDataChanged();
    } catch (error: any) {
      console.error('[ComplianceCalendar] Failed to submit checklist from calendar:', error);
      toast.error(error?.message || 'Failed to submit checklist.');
      throw error;
    }
  }, [activeTenancy?.id, selectedChecklist, handleDataChanged]);

  const handleViewEvent = (event: ComplianceEvent) => {
    setSelectedEvent(event);
    if (event.type === 'assurance-task') setPerformOpen(true);
    else if (event.type === 'asset-review') setReviewAssetOpen(true);
    else if (event.type === 'risk-review') setReviewRiskOpen(true);
    else if (event.type === 'document-review') setReviewDocOpen(true);
    else if (event.type === 'policy-review') setPolicyReviewOpen(true);
    else if (event.type === 'policy') setPolicyOpen(true);
    else if (event.type === 'checklist') {
      setSelectedChecklist(null);
      setChecklistOpen(true);
      void loadChecklistForEvent(event);
    }
    else if (event.type === 'corrective-action') setCorrectiveActionOpen(true);
    else if (event.type === 'process') {
      // Open the review dialog - it will load the process data internally
      setProcessOpen(true);
    }
  };

  // Load data from service based on user filter
  useEffect(() => {
    // Skip if no active tenancy available
    const fetchId = ++fetchIdRef.current; // Increment for each fetch
    if (!activeTenancy || !activeTenancy.id) {
      console.log('Waiting for active tenancy...');
      return;
    }

    // Reset state before loading new data
    setEvents([]);
    setLoading(true);
    setError(null);

    // Use tenancy ID directly
    const companyId = activeTenancy.id;
    if (!companyId) {
      if (fetchId !== fetchIdRef.current) return;
      setError("No company ID found. Please select a tenancy.");
      setLoading(false);
      return;
    }

    // Calculate date range based on current view and date
    const dateFilter = getDateRangeForView(view, currentDate);

    // Create base filter object
    const baseFilter = {
      startDate: dateFilter.startDate,
      endDate: dateFilter.endDate,
      view: view.toLowerCase() as 'day' | 'week' | 'month' | 'year',
    };

    // Decide whether to fetch all events or just user-specific events
    if (userFilter === 'mine') {
      // Create filter object for user-specific events
      const userFilterObj = {
        ...baseFilter,
        userId: currentUserId,
      };

      console.log('[Compliance Calendar] Fetching user events:', userFilterObj);
      console.log('[Compliance Calendar] Current user:', tenancyCurrentUser);

      // Fetch only current user's events with date filtering
      getUserComplianceEvents(companyId, derivedTenancyUser as any, userFilterObj)
        .then(userEvents => {
          if (fetchId !== fetchIdRef.current) return;
          console.log(`[Compliance Calendar] Loaded ${userEvents.length} user events for tenancy: ${activeTenancy.name}`, userEvents);
          console.log(`[Compliance Calendar] Setting events for tenancy: ${activeTenancy.name}`)
          setEvents(userEvents);
          console.log(`[Compliance Calendar] Set events for tenancy: ${activeTenancy.name}`, userEvents)
          setLoading(false);
        })
        .catch(err => {
          if (fetchId !== fetchIdRef.current) return;
          console.error("Error fetching user compliance events:", err);
          setError("Failed to load compliance data. Please try again later.");
          setEvents([]);
          setLoading(false);
        });
    } else {
      console.log('[Compliance Calendar] Fetching all events with filter:', baseFilter);

      getComplianceData(companyId, baseFilter)
        .then(data => {
          if (fetchId !== fetchIdRef.current) return;
          console.log(`[Compliance Calendar] Loaded ${data.allEvents?.length ?? 0} events for tenancy: ${activeTenancy.name}`);
          setEvents(data.allEvents ?? []);
          setLoading(false);
        })
        .catch(err => {
          if (fetchId !== fetchIdRef.current) return;
          console.error("Error fetching compliance data:", err);
          setError("Failed to load compliance data. Please try again later.");
          setEvents([]);
          setLoading(false);
        });
    }
  }, [userFilter, currentUserId, activeTenancy, view, currentDate]);


  // Navigation handlers
  const handleMonthClick = (month: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(month);
    setCurrentDate(newDate);
    setView('Month');
  };

  // Persist view to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('complianceCalendarView', view);
    }
  }, [view]);

  // Initialize view state from localStorage if present
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedView = window.localStorage.getItem('complianceCalendarView');
      if (storedView && VIEWS.includes(storedView as CalendarView)) {
        setView(storedView as CalendarView);
      }
    }
  }, []); // Run only once on mount

  const [helpContent, setHelpContent] = useState(null);
  useEffect(() => {
    getHelpContentByPage('compliance.calendar').then(setHelpContent);
  }, []);

  return (
    <div className="w-full min-h-[80vh] bg-white dark:bg-zinc-900 rounded-xl shadow-sm mx-auto mt-2 sm:mt-4 md:mt-8 px-3 sm:px-4 md:px-8 py-4 sm:py-6 flex flex-col gap-4 sm:gap-6">
      {/* Header - Streamlined single row layout */}
      <div className="flex flex-col gap-3 border-b pb-4">
        {/* Main header row */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Title + Help */}
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="title text-lg sm:text-xl font-semibold whitespace-nowrap">
              Compliance Calendar
            </h2>
            <HelpPopoverHover config={helpContent?.['page.title']} />
          </div>

          {/* Right: Action buttons - consolidated */}
          <div className="flex items-center gap-2">
            {/* Add Task - Primary action, always visible */}
            <Button 
              variant="default" 
              size="sm"
              className="h-9 px-3" 
              onClick={() => setAddTaskModalOpen(true)}
            >
              <Plus className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Add Task</span>
            </Button>
            
            {/* Add to calendar dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-9 px-3" 
                  aria-busy={signingIcs} 
                  disabled={signingIcs}
                >
                  <CalendarPlus className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Add to calendar</span>
                  {signingIcs && <span className="ml-1 text-xs opacity-70">...</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={copySubscriptionLink} className="cursor-pointer">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy link
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openInGoogleCalendar} className="cursor-pointer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Google Calendar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openInAppleCalendar} className="cursor-pointer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apple Calendar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openInOutlook365} className="cursor-pointer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Outlook 365
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More actions dropdown - consolidates secondary actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {/* Starter plan actions */}
                {isStarterPlan && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/assurancelibrary')} className="cursor-pointer">
                      <ListTodo className="w-4 h-4 mr-2" />
                      View All Tasks
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleExportProgress} 
                      className="cursor-pointer"
                      disabled={exportingProgress}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Progress
                      {exportingProgress && <span className="ml-1 text-xs opacity-70">...</span>}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Secondary row: Date navigation + filters */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Date navigation */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToPrev} aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToToday} aria-label="Today">
              <CalendarIcon className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToNext} aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <span className="text-base sm:text-lg font-medium ml-2 text-muted-foreground">
              {getTitle(view, currentDate)}
            </span>
          </div>

          {/* Right: View and filter controls */}
          <div className="flex items-center gap-2">
            {/* User Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2">
                  <User className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline text-sm">
                    {userFilter === 'all' ? 'All' : 'Mine'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {USER_FILTERS.map(f => (
                  <DropdownMenuItem key={f.value} onClick={() => setUserFilter(f.value as 'all' | 'mine')}>
                    {f.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* View Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2">
                  {view === 'Day' && <Rows className="w-4 h-4 sm:mr-1.5" />}
                  {view === 'Week' && <List className="w-4 h-4 sm:mr-1.5" />}
                  {view === 'Month' && <Grid className="w-4 h-4 sm:mr-1.5" />}
                  {view === 'Year' && <LayoutGrid className="w-4 h-4 sm:mr-1.5" />}
                  {view === 'Monthly List' && <List className="w-4 h-4 sm:mr-1.5" />}
                  <span className="hidden sm:inline text-sm">{view}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {VIEWS.map(v => (
                  <DropdownMenuItem key={v} onClick={() => setView(v)}>
                    {v}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    {/* Calendar grid display */}
    <div className="flex-1 w-full">
      {/* Loading indicator */}
      {loading && (
        <div className="w-full flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading compliance data...</p>
        </div>
      )}
      {/* Calendar grid display */}
      <div className="flex-1 w-full">
        {/* Loading indicator */}
        {loading && (
          <div className="w-full flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading compliance data...</p>
          </div>
        )}

        {/* Error message */}
        {error && !loading && (
          <div className="w-full rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {view === 'Month' && (
        <MonthView
          date={currentDate}
          onViewEvent={handleViewEvent}
          onDayClick={date => {
            setView('Day');
            setCurrentDate(date);
          }}
          userFilter={userFilter}
          currentUser={currentUser}
          events={events}
        />
      )}
            {view === 'Week' && <WeekView date={currentDate} onViewEvent={handleViewEvent} userFilter={userFilter} currentUser={currentUser} events={events} />}
            {view === 'Day' && <DayView date={currentDate} onViewEvent={handleViewEvent} userFilter={userFilter} currentUser={currentUser} events={events} />}
            {view === 'Monthly List' && <MonthlyListView date={currentDate} onViewEvent={handleViewEvent} userFilter={userFilter} currentUser={currentUser} events={events} />}
            {view === 'Year' && (
              <YearView
                date={currentDate}
                onViewEvent={handleViewEvent}
                onMonthClick={month => {
                  setView('Month');
                  setCurrentDate(new Date(currentDate.getFullYear(), month, 1));
                }}
                onDayClick={date => {
                  setView('Day');
                  setCurrentDate(date);
                }}
                userFilter={userFilter}
                currentUser={currentUser}
                events={events}
              />
            )}
          </>
        )}
      </div>
      {/* Dialog for Perform Task */}
      <PerformTaskDialog
        open={performOpen}
        onOpenChange={setPerformOpen}
        selectedEvent={selectedEvent}
        onDataChanged={handleDataChanged}
      />
      <ReviewAssetDialog
        open={reviewAssetOpen}
        onOpenChange={setReviewAssetOpen}
        selectedEvent={selectedEvent}
        onDataChanged={handleDataChanged}
        helpData={helpContent}
      />
      <ReviewRiskDialog
        open={reviewRiskOpen}
        onOpenChange={setReviewRiskOpen}
        selectedEvent={selectedEvent}
        onDataChanged={handleDataChanged}
      />
      <ReviewDocDialog
        key={selectedEvent ? `${selectedEvent.id}-${selectedEvent.title || selectedEvent.description || ''}` : 'empty'}
        open={reviewDocOpen}
        onOpenChange={setReviewDocOpen}
        selectedEvent={selectedEvent}
        onDataChanged={handleDataChanged}
        helpData={helpContent}
      />
      {/* <PolicyReviewDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        selectedEvent={selectedEvent}
      /> */}
      <PolicyReviewDialog
        open={policyReviewOpen}
        onOpenChange={setPolicyReviewOpen}
        selectedEvent={selectedEvent}
        onDataChanged={handleDataChanged}
      />
      <ChecklistCompleteDialog
        open={checklistOpen}
        onOpenChange={(open) => {
          setChecklistOpen(open);
          if (!open) {
            setSelectedChecklist(null);
          } else if (selectedEvent && selectedEvent.type === 'checklist' && !loadingChecklist) {
            void loadChecklistForEvent(selectedEvent);
          }
        }}
        checklist={loadingChecklist ? null : selectedChecklist}
        onSubmit={handleChecklistSubmit}
      />
      <CorrectiveActionDialog
        open={correctiveActionOpen}
        onOpenChange={setCorrectiveActionOpen}
        selectedEvent={selectedEvent}
      />
      <ReviewProcessDialog
        open={processOpen}
        onOpenChange={setProcessOpen}
        selectedEvent={selectedEvent}
        onDataChanged={handleDataChanged}
      />
      <AddAssuranceTaskModal
        open={addTaskModalOpen}
        onOpenChange={setAddTaskModalOpen}
        onTaskAdded={handleDataChanged}
      />
      <AlertDialog open={outlookHelpOpen} onOpenChange={setOutlookHelpOpen}>
        <AlertDialogContent className='bg-card'>
          <AlertDialogHeader>
            <AlertDialogTitle>Subscribe in Outlook 365</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-left">
                <p>We will open Outlook’s Add calendar page. Then:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Click <strong>Subscribe from web</strong>.</li>
                  <li>Paste the subscription URL we copy to your clipboard.</li>
                  <li>Give it a name (e.g., Compliance Calendar) and save.</li>
                </ol>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOutlook365}>I’ve read it – Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={googleHelpOpen} onOpenChange={setGoogleHelpOpen}>
        <AlertDialogContent className='bg-card'>
          <AlertDialogHeader>
            <AlertDialogTitle>Add to Google Calendar</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-left">
                <p>We will open Google Calendar’s Add by URL page. Then:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Paste the subscription URL we copy to your clipboard.</li>
                  <li>Click <strong>Add calendar</strong>.</li>
                </ol>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmGoogleCalendar}>I’ve read it – Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </div>
  );
};

// Utility for month grid
function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

const MonthView: React.FC<{
  date: Date;
  onViewEvent: (event: ComplianceEvent) => void;
  onDayClick?: (date: Date) => void;
  userFilter: 'all' | 'mine';
  currentUser: string;
  events: ComplianceEvent[];
}> = ({ date, onViewEvent, onDayClick, userFilter, currentUser, events }) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = new Date(year, month, 1).getDay();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  // Filter events for this month (parse event date as local time, not UTC)
  const eventsByDay: Record<number, ComplianceEvent[]> = {};
  events.forEach(event => {
    // Parse date as local, not UTC
    const [y, m, d] = event.date.split('-').map(Number);
    if (y === year && m - 1 === month) {
      if (userFilter === 'mine' && event.assignee !== tenancyCurrentUser?.userID) return;
      if (!eventsByDay[d]) eventsByDay[d] = [];
      eventsByDay[d].push(event);
    }
  });

  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = Array(firstDay).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    week.push(day);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) weeks.push([...week, ...Array(7 - week.length).fill(null)]);

  // Mobile detection
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  const dayNames = isMobile 
    ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] 
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="w-full h-[calc(100vh-180px)] min-h-[300px] overflow-auto pb-4">
      <div className="min-w-[320px] sm:min-w-[480px] md:min-w-0">
        <table className="w-full h-full table-fixed border-separate border-spacing-0">
          <thead>
            <tr className="text-muted-foreground border-b sticky top-0 bg-background z-20">
              {dayNames.map((d, index) => (
                <th 
                  key={index} 
                  className={clsx(
                    'py-2 font-medium text-center text-xs md:text-sm border-r last:border-r-0 border-muted-foreground/20',
                    'sticky top-0 bg-background z-10'
                  )}
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, i) => (
              <tr key={i} className="border-b last:border-b-0 border-muted-foreground/20">
                {week.map((day, j) => (
                  <td
                    key={j}
                    className={clsx(
                      'relative align-top transition-colors cursor-pointer',
                      'h-[calc((100vh-180px)/6)] min-h-[48px] sm:min-h-[60px] md:min-h-[80px] p-0',
                      'border-b border-muted-foreground/20',
                      j !== 6 && 'border-r',
                      i === weeks.length - 1 && j === 6 && 'last:!border-r-0',
                      day && todayDate === day && todayMonth === month && todayYear === year
                        ? 'bg-primary/10 text-primary font-bold shadow-sm' : 'hover:bg-accent/50',
                      !day && 'bg-muted/30',
                      'group',
                      'text-[10px] sm:text-xs'  // Smaller text on mobile
                    )}
                    onClick={e => {
                      if (!day) return;
                      if ((e.target as HTMLElement).closest('.calendar-event-cell')) return;
                      if (onDayClick) {
                        onDayClick(new Date(year, month, day));
                      }
                    }}
                  >
                    <div className={clsx(
                      'absolute top-0.5 left-0.5 sm:top-1 sm:left-1 md:top-2 md:left-2 text-[10px] sm:text-xs font-semibold select-none',
                      'flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-full',
                      day && todayDate === day && todayMonth === month && todayYear === year 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground group-hover:text-foreground',
                      'leading-none'  // Prevent line height issues
                    )}>
                      {day || ''}
                    </div>
                    
                    {day && eventsByDay[day] && (
                      <div className="absolute inset-0 pt-5 sm:pt-6 md:pt-8 pb-0.5 px-0.5 overflow-hidden">
                        <div className="h-full overflow-y-auto no-scrollbar">
                          <div className="space-y-0 sm:space-y-0.5">
                            {eventsByDay[day].map((event, idx) => (
                              <div
                                key={idx}
                                className={clsx(
                                  'calendar-event-cell text-[8px] sm:text-[10px] md:text-xs px-1 py-0.5 sm:px-1.5 sm:py-1 rounded',
                                  eventTypeStyles[event.type as keyof typeof eventTypeStyles] || 'bg-gray-100',
                                  'cursor-pointer hover:opacity-90 transition-opacity',
                                  'flex items-center gap-1 truncate',
                                  'active:scale-95 transform transition-transform',
                                  'border border-white/20 shadow-sm'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewEvent(event);
                                }}
                              >
                                <div className="flex-shrink-0">
                                  {React.cloneElement(
                                    eventTypeIcons[event.type as keyof typeof eventTypeIcons],
                                    { className: 'w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 flex-shrink-0' }
                                  )}
                                </div>
                                <span className="truncate">
                                  {event.title.length > 15 
                                      ? `${event.title.substring(0, 25)}...`
                                      : event.title}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Global styles are added via CSS modules or global CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `
      }} />
    </div>
  );
};

const WeekView: React.FC<{ date: Date; onViewEvent: (event: ComplianceEvent) => void; userFilter: 'all' | 'mine'; currentUser: string; events: ComplianceEvent[] }> = ({ date, onViewEvent, userFilter, currentUser, events }) => {
  // Start of week (Sunday)
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  // 6am to 8pm
  const hours = Array.from({ length: 15 }, (_, i) => i + 6);
  // Gather events for each day in week
  const weekDays: Date[] = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  // Map events to each day
  const eventsByDay: Record<string, ComplianceEvent[]> = {};
  events.forEach(event => {
    const [y, m, d] = event.date.split('-').map(Number);
    weekDays.forEach(dayObj => {
      if (
        y === dayObj.getFullYear() &&
        m - 1 === dayObj.getMonth() &&
        d === dayObj.getDate()
      ) {
        if (userFilter === 'mine' && event.assignee !== tenancyCurrentUser?.userID) return;
        const key = dayObj.toDateString();
        if (!eventsByDay[key]) eventsByDay[key] = [];
        eventsByDay[key].push(event);
      }
    });
  });
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="w-14"></th>
            {weekDays.map((d, i) => (
              <th key={i} className="text-center font-semibold text-sm pb-2">
                <div className={clsx('flex flex-col items-center', today.toDateString() === d.toDateString() ? 'text-primary font-bold' : '')}>
                  <>
                    <span>{d.toLocaleDateString(userLocale, { weekday: 'short' })}</span>
                    <span>{d.getDate()}</span>
                  </>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hours.map(hour => (
            <tr key={hour}>
              <td className="text-xs text-muted-foreground pr-2 text-right align-top py-2 w-14">{hour}:00</td>
              {weekDays.map((d, i) => {
                const key = d.toDateString();
                return (
                  <td key={i} className="h-12 border border-muted-foreground/10 align-top bg-white dark:bg-zinc-900 hover:bg-accent transition-colors relative">
                    {/* Render events for this day */}
                    {eventsByDay[key] && hour === 9 && (
                      <div className="flex flex-col gap-1">
                        {eventsByDay[key].map((event, idx) => (
                          <div
                            key={idx}
                            className={clsx(
                              'truncate px-2 py-0.5 rounded text-xs cursor-pointer',
                              eventTypeStyles[event.type]
                            )}
                            title={event.description || event.title}
                            onClick={() => onViewEvent(event)}
                          >
                            {event.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DayView: React.FC<{ date: Date; onViewEvent: (event: ComplianceEvent) => void; userFilter: 'all' | 'mine'; currentUser: string; events: ComplianceEvent[] }> = ({ date, onViewEvent, userFilter, currentUser, events }) => {
  // Gather events for this day
  const filteredEvents = events.filter(event => {
    const [y, m, d] = event.date.split('-').map(Number);
    const match = (
      y === date.getFullYear() &&
      m - 1 === date.getMonth() &&
      d === date.getDate()
    );
    if (!match) return false;
    if (userFilter === 'mine' && event.assignee !== tenancyCurrentUser?.id) return false;
    return true;
  });

  return (
    <div className="flex flex-col items-center w-full px-2 sm:px-0">
      <span className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 mt-2 text-center px-2">{date.toLocaleDateString(userLocale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
      <div className="w-full max-w-2xl flex flex-col gap-3 sm:gap-4 items-center">
        {filteredEvents.length === 0 ? (
          <div className="w-full flex flex-col items-center justify-center rounded-lg bg-muted/40 py-16">
            <span className="text-muted-foreground text-lg">No events for this day.</span>
          </div>
        ) : (
          filteredEvents.map((event, idx) => (
            <div
              key={idx}
              className="w-full bg-white dark:bg-zinc-900 rounded-lg sm:rounded-xl shadow border flex flex-col sm:flex-row items-stretch gap-3 sm:gap-6 p-3 sm:px-6 sm:py-4 relative hover:shadow-md transition-shadow min-h-[100px] sm:min-h-[120px]"
            >
              {/* Icon and Type - Mobile optimized */}
              <div className="flex flex-row sm:flex-col items-center sm:justify-center gap-3 sm:gap-0">
                <div className="flex flex-col items-center justify-center">
                  {React.cloneElement(
                    eventTypeIcons[event.type],
                    { className: 'w-4 h-4 sm:w-5 sm:h-5' }
                  )}
                </div>
                <div className="flex flex-row sm:flex-col items-center gap-1 sm:gap-2">
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold uppercase tracking-wide ${eventTypeStyles[event.type]}`}>
                    {eventTypeLabels[event.type]}
                  </span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium">
                    {event.time || 'All Day'}
                  </span>
                </div>
              </div>
              {/* Main content - DED-1484: Added justify-center to vertically center the title */}
              <div className="flex-1 flex flex-col justify-center gap-1 sm:gap-1 min-w-0">
                <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100 break-words line-clamp-2">{event.title}</span>
              </div>
              {/* Assignee + Action */}
              <div className="flex flex-row items-center justify-between gap-3 w-full sm:w-auto sm:flex-col sm:items-center sm:gap-4">
                {event.assignee && (() => {
                  const assigneeName = getAssigneeName(event.assignee);
                  // Hide if deleted user or empty
                  if (assigneeName === 'Deleted User' || !assigneeName) return null;
                  return (
                    <div className="flex flex-col items-center">
                      <span className="font-bold text-gray-700 text-sm sm:text-base">
                        {assigneeName.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                      <span className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                        {assigneeName}
                      </span>
                    </div>
                  );
                })()}
                <button 
                  className="p-1.5 sm:p-2 rounded-full bg-primary text-white hover:bg-primary/90 transition shadow-md flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0" 
                  onClick={() => onViewEvent(event)} 
                  title="View Details"
                  aria-label="View details"
                >
                  <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const MonthlyListView: React.FC<{ date: Date; onViewEvent: (event: ComplianceEvent) => void; userFilter: 'all' | 'mine'; currentUser: string; events: ComplianceEvent[] }> = ({ date, onViewEvent, userFilter, currentUser, events }) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  // Gather all events for this month
  const filteredEvents = events.filter(event => {
    const [y, m] = event.date.split('-').map(Number);
    if (!(y === year && m - 1 === month)) return false;

    if (userFilter === 'mine' &&
      event.assignee !== tenancyCurrentUser?.id) {
      return false;
    }
    return true;
  });

  // Sort events: overdue first, then by day ascending
  const now = new Date();
  now.setHours(0, 0, 0, 0);  // Reset to midnight for proper day comparison
  const overdueEvents: ComplianceEvent[] = [];
  const upcomingEvents: ComplianceEvent[] = [];
  filteredEvents.forEach(event => {
    const eventDate = new Date(event.date);
    console.log(`Event date string: ${event.date}, parsed as: ${eventDate.toISOString()}`);
    if (eventDate < now) {
      overdueEvents.push(event);
    } else {
      upcomingEvents.push(event);
    }
  });
  // Group by day after sorting
  const groupByDay = (events: ComplianceEvent[]) => {
    const byDay: Record<number, ComplianceEvent[]> = {};
    events.forEach(event => {
      const day = Number(event.date.split('-')[2]);
      console.log(`Grouping event by day: ${day} from date string: ${event.date}`);
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(event);
    });
    return byDay;
  };
  const overdueByDay = groupByDay(overdueEvents);
  const upcomingByDay = groupByDay(upcomingEvents);
  const daysOverdue = Object.keys(overdueByDay).map(Number).sort((a, b) => a - b);
  const daysUpcoming = Object.keys(upcomingByDay).map(Number).sort((a, b) => a - b);

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col gap-3 sm:gap-6 mt-2 sm:mt-4 px-2 sm:px-0">
      {/* Overdue Events (on top) */}
      {daysOverdue.map(day => (
        <div key={"overdue-" + day} className="w-full flex flex-col gap-1 sm:gap-2">
          <div className="text-sm sm:text-lg font-bold text-primary mb-1 sm:mb-2 px-2 sm:px-0">
            {new Date(year, month, day).toLocaleDateString(userLocale, { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric', 
              year: new Date().getFullYear() !== year ? 'numeric' : undefined 
            }).replace(/,/g, '')}
          </div>
          <div className="flex flex-col gap-2 sm:gap-4">
            {overdueByDay[day].map((event, idx) => (
              <div
                key={idx}
                className="w-full bg-white dark:bg-zinc-900 rounded-lg sm:rounded-xl shadow border flex flex-col sm:flex-row items-stretch gap-3 sm:gap-6 p-3 sm:px-6 sm:py-4 relative hover:shadow-md transition-shadow min-h-[100px] sm:min-h-[120px] border-l-4 border-red-500"
                style={{ borderLeft: '4px solid #ef4444' }}
              >
                {/* Icon and Type - Mobile optimized */}
                <div className="flex flex-row sm:flex-col items-center sm:justify-center gap-3 sm:gap-0">
                  <div className="hidden sm:flex flex-col items-center justify-center">
                    {eventTypeIcons[event.type]}
                  </div>
                  <div className="flex flex-row sm:flex-col items-center gap-1 sm:gap-2">
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold uppercase tracking-wide ${eventTypeStyles[event.type]}`}>
                      {eventTypeLabels[event.type]}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground font-medium">
                      {event.time || 'All Day'}
                    </span>
                  </div>
                </div>
                {/* Main content */}
                <div className="flex-1 flex flex-col justify-center gap-0.5 sm:gap-1 min-w-0">
                  <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100 break-words line-clamp-2">
                    {event.title}
                  </span>
                </div>
                {/* Assignee + Action */}
                <div className="flex flex-row items-center justify-between gap-3 w-full sm:w-auto sm:flex-col sm:items-center sm:gap-4">
                  {event.assignee && (() => {
                    const assigneeName = getAssigneeName(event.assignee);
                    if (assigneeName === 'Deleted User' || !assigneeName) return null;
                    return (
                      <div className="flex flex-col items-center gap-0">
                        <span className="font-bold text-gray-700 text-sm sm:text-base">
                          {assigneeName.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground mt-1">{assigneeName}</span>
                      </div>
                    );
                  })()}
                  <button 
                    className="p-1.5 sm:p-2 rounded-full bg-primary text-white hover:bg-primary/90 transition shadow-md flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0" 
                    onClick={() => onViewEvent(event)} 
                    title="View Details"
                    aria-label="View details"
                  >
                    <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {/* Upcoming Events (below) */}
      {daysUpcoming.map(day => (
        <div key={"upcoming-" + day} className="w-full flex flex-col gap-1 sm:gap-2">
          <div className="text-sm sm:text-lg font-bold text-primary mb-1 sm:mb-2 px-2 sm:px-0">
            {new Date(year, month, day).toLocaleDateString(userLocale, { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric', 
              year: new Date().getFullYear() !== year ? 'numeric' : undefined 
            }).replace(/,/g, '')}
          </div>
          <div className="flex flex-col gap-2 sm:gap-4">
            {upcomingByDay[day].map((event, idx) => (
              <div
                key={idx}
                className="w-full bg-white dark:bg-zinc-900 rounded-lg sm:rounded-xl shadow border flex flex-col sm:flex-row items-stretch gap-3 sm:gap-6 p-3 sm:px-6 sm:py-4 relative hover:shadow-md transition-shadow min-h-[100px] sm:min-h-[120px] border-l-4 border-primary"
              >
                {/* Icon and Type - Mobile optimized */}
                <div className="flex flex-row sm:flex-col items-center sm:justify-center gap-3 sm:gap-0">
                  <div className="hidden sm:flex flex-col items-center justify-center">
                    {eventTypeIcons[event.type]}
                  </div>
                  <div className="flex flex-row sm:flex-col items-center gap-1 sm:gap-2">
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold uppercase tracking-wide ${eventTypeStyles[event.type]}`}>
                      {eventTypeLabels[event.type]}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground font-medium">
                      {event.time || 'All Day'}
                    </span>
                  </div>
                </div>
                {/* Main content */}
                <div className="flex-1 flex flex-col justify-center gap-0.5 sm:gap-1 min-w-0">
                  <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100 break-words line-clamp-2">
                    {event.title}
                  </span>
                </div>
                {/* Assignee + Action */}
                <div className="flex flex-row items-center justify-between gap-3 w-full sm:w-auto sm:flex-col sm:items-center sm:gap-4">
                  {event.assignee && (() => {
                    const assigneeName = getAssigneeName(event.assignee);
                    if (assigneeName === 'Deleted User' || !assigneeName) return null;
                    return (
                      <div className="flex flex-col items-center gap-0">
                        <span className="font-bold text-gray-700 text-sm sm:text-base">
                          {assigneeName.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground mt-1">{assigneeName}</span>
                      </div>
                    );
                  })()}
                  <button 
                    className="p-1.5 sm:p-2 rounded-full bg-primary text-white hover:bg-primary/90 transition shadow-md flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0" 
                    onClick={() => onViewEvent(event)} 
                    title="View Details"
                    aria-label="View details"
                  >
                    <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {daysOverdue.length === 0 && daysUpcoming.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg bg-muted/40 py-16">
          <span className="text-muted-foreground text-lg">No events for this month.</span>
        </div>
      )}
    </div>
  );
};

const YearView: React.FC<{
  date: Date;
  onViewEvent: (event: ComplianceEvent) => void;
  onMonthClick?: (month: number) => void;
  onDayClick?: (date: Date) => void;
  userFilter: 'all' | 'mine';
  currentUser: string;
  events: ComplianceEvent[];
}> = ({ date, onViewEvent, onMonthClick, onDayClick, userFilter, currentUser, events }) => {
  const year = date.getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => new Date(year, i));

  // Helper: days in month
  function getDaysInMonth(year: number, month: number) {
    return new Date(year, month + 1, 0).getDate();
  }

  // Helper: get events for a given month and day
  function getEventsForDay(y: number, m: number, d: number) {
    return events.filter(event => {
      const [ey, em, ed] = event.date.split('-').map(Number);
      if (!(ey === y && em - 1 === m && ed === d)) return false;
      if (userFilter === 'mine' && event.assignee !== tenancyCurrentUser?.id) return false;
      return true;
    });
  }

  // Helper: day label
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 p-4">
      {months.map((monthDate, idx) => {
        const m = monthDate.getMonth();
        const days = getDaysInMonth(year, m);
        const firstDay = new Date(year, m, 1).getDay();
        const todayObj = new Date();
        return (
          <div
            key={m}
            className="bg-white dark:bg-zinc-900 rounded-2xl shadow-lg p-6 flex flex-col border border-gray-100 hover:shadow-2xl transition-shadow min-h-[340px]"
          >
            <button
              className="text-center font-bold text-xl text-primary mb-4 tracking-wide uppercase hover:underline focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
              style={{ cursor: onMonthClick ? 'pointer' : undefined }}
              onClick={onMonthClick ? () => onMonthClick(m) : undefined}
              tabIndex={0}
              aria-label={`View ${monthDate.toLocaleString(userLocale, { month: 'long' })}`}
            >
              {monthDate.toLocaleString(userLocale, { month: 'long' })}
            </button>
            <table className="w-full table-fixed border-separate border-spacing-y-1">
              <thead>
                <tr>
                  {dayLabels.map((d, i) => (
                    <th key={i} className="text-xs font-semibold text-gray-400 pb-2">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.ceil((days + firstDay) / 7) }).map((_, weekIdx) => (
                  <tr key={weekIdx}>
                    {Array.from({ length: 7 }).map((_, dayIdx) => {
                      const dayNum = weekIdx * 7 + dayIdx - firstDay + 1;
                      const isOtherMonth = dayNum < 1 || dayNum > days;
                      const isToday =
                        dayNum === todayObj.getDate() &&
                        m === todayObj.getMonth() &&
                        year === todayObj.getFullYear();
                      const events = (!isOtherMonth && dayNum)
                        ? getEventsForDay(year, m, dayNum)
                        : [];
                      return (
                        <td
                          key={dayIdx}
                          className={clsx(
                            'relative text-center align-top p-0 h-10 w-10 group', // 👈 added 'group' here
                            isOtherMonth ? 'text-gray-200' : 'text-gray-700',
                            isToday && !isOtherMonth ? 'bg-primary/10 font-bold shadow' : '',
                            events.length > 0 && !isOtherMonth ? 'cursor-pointer hover:bg-accent rounded-lg transition' : ''
                          )}
                          onClick={() => {
                            if (!isOtherMonth && dayNum > 0 && dayNum <= days) {
                              const clickedDate = new Date(year, m, dayNum);
                              if (onDayClick) {
                                onDayClick(clickedDate);
                              } else if (events.length > 0) {
                                onViewEvent(events[0]);
                              }
                            }
                          }}
                          title={events.length > 0 && !isOtherMonth ? events.map(e => e.title).join(', ') : undefined}
                        >
                          <div className="flex flex-col items-center justify-center h-full">
                            <span
                              className={clsx(
                                'inline-block text-base leading-6 select-none transition-colors',
                                isToday && !isOtherMonth ? 'text-primary group-hover:text-foreground' : ''
                              )}
                            >
                              {dayNum > 0 && dayNum <= days ? dayNum : ''}
                            </span>

                            {/* Dot for events */}
                            <div className="flex flex-row gap-0.5 items-center justify-center mt-1">
                              {events.length > 0 && (
                                <span
                                  className={clsx(
                                    'w-2 h-2 rounded-full',
                                    'bg-white dark:bg-zinc-900',
                                    'border border-black dark:border-white',
                                    eventTypeStyles[events[0].type]
                                  )}
                                  title={`${events.length} event${events.length > 1 ? 's' : ''}`}
                                ></span>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default ComplianceCalendar;

// -- Add your Policy logic and modal here
